import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { StoryCard } from "@/components/StoryCard";
import { Trophy, Calendar, Wand2 } from "lucide-react";

export default function Contest() {
  const [contest, setContest] = useState(null);
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const load = async () => {
      const c = await api.get("/contests/active").then(r => r.data).catch(() => null);
      setContest(c);
      if (c) {
        const e = await api.get(`/stories?contest_id=${c.contest_id}`).then(r => r.data).catch(() => []);
        setEntries(e);
      }
    };
    load();
  }, []);

  if (!contest) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">No active contest.</div>;

  const days = Math.max(0, Math.round((new Date(contest.ends_at) - Date.now()) / (1000*60*60*24)));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
      <div className="relative rounded-3xl overflow-hidden border border-rose-500/30 bg-gradient-to-br from-rose-500/12 via-transparent to-amber-500/10 p-8 md:p-14 mb-12 grain">
        <div className="contest-pill inline-block mb-4">Monthly holiday challenge · new theme every month</div>
        <h1 data-testid="contest-title" className="font-serif text-3xl md:text-5xl font-bold leading-tight tracking-tight max-w-3xl">{contest.title}</h1>
        <p className="mt-4 text-slate-300 max-w-2xl leading-relaxed">{contest.description}</p>
        <div className="mt-6 flex flex-wrap gap-6 text-sm">
          <span className="flex items-center gap-2 text-rose-300"><Calendar className="h-4 w-4" /> {days <= 1 ? "Ends tonight" : `${days} days left`}</span>
          <span className="flex items-center gap-2 text-amber-300"><Trophy className="h-4 w-4" /> Winner gets {(contest.prizes?.[0]?.credits || 1000).toLocaleString()} gems</span>
        </div>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button data-testid="contest-submit" asChild className="bg-rose-500 hover:bg-rose-400 text-white font-semibold rounded-full h-12 px-7">
            <Link to={`/create?contest=${contest.contest_id}`} data-testid="contest-enter-ai"><Wand2 className="h-4 w-4 mr-2" /> Submit your entry</Link>
          </Button>
          <Link to={`/write?contest=${contest.contest_id}`} data-testid="contest-enter-write" className="ml-4 text-sm text-rose-300 underline underline-offset-4">or write it yourself</Link>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-12">
        {contest.prizes.map((p) => (
          <div key={p.place} data-testid="contest-prize" className="rounded-2xl bg-[#0F1117] border border-white/10 p-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-rose-400 mb-1">{p.place}</div>
            <div className="font-serif text-3xl font-bold text-amber-300">{p.credits.toLocaleString()}</div>
            <div className="text-xs text-slate-500 mt-1">gems · top-voted approved story when the month ends</div>
          </div>
        ))}
      </div>

      <h2 className="font-serif text-2xl md:text-3xl font-semibold mb-6">Current entries</h2>
      {entries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 p-10 text-center text-slate-500">
          No entries yet — be the first to submit!
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
          {entries.map((s) => <StoryCard key={s.story_id} story={s} />)}
        </div>
      )}
    </div>
  );
}
